import { FC, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
import styles from './charttooltip.module.sass';

// ══════════════════════════════════════════════════════════════════
// CHART TOOLTIP — Unified tooltip for all DS chart components
//
// Rendered via a portal into document.body with position:fixed so it
// escapes any overflow:hidden ancestor (ChartFrame, scroll containers).
// Pass viewport coordinates (from getBoundingClientRect) as x/y.
// ══════════════════════════════════════════════════════════════════

export interface ChartTooltipRow {
  /** Color swatch (CSS color string) */
  color: string;
  /** Series / segment label */
  label: string;
  /** Formatted value */
  value: string;
  /** Optional delta string (e.g. "+12.5%") */
  delta?: string;
  /** Delta direction for coloring */
  deltaDir?: 'up' | 'down' | 'neutral';
}

export interface ChartTooltipProps {
  /** Whether tooltip is visible */
  visible: boolean;
  /** Header text (date, category label, etc.) */
  header?: string;
  /** Structured rows with swatches */
  rows?: ChartTooltipRow[];
  /** Or: arbitrary content for simple tooltips */
  children?: ReactNode;
  /** Viewport X in px (from getBoundingClientRect) */
  x: number;
  /** Viewport Y in px (from getBoundingClientRect) */
  y: number;
  /** @deprecated — ignored, kept for API compat */
  flip?: boolean;
  /** Anchor to bottom instead of top */
  anchorBottom?: boolean;
  /** Additional CSS class */
  className?: string;
}

export const ChartTooltip: FC<ChartTooltipProps> = ({
  visible,
  header,
  rows,
  children,
  x,
  y,
  anchorBottom = false,
  className,
}) => {
  if (!visible) return null;

  // Clamp left so the tooltip never bleeds off screen.
  // max-width is 280px + 8px margin on each side = 296px.
  const clampedLeft = `clamp(8px, calc(${x}px - 70px), calc(100vw - 296px))`;
  const verticalTransform = anchorBottom ? 'translateY(8px)' : 'translateY(-100%)';

  const node = (
    <div
      className={classNames(styles.tip, className)}
      style={{ left: clampedLeft, top: y, transform: verticalTransform }}
      role="tooltip"
    >
      {header && <div className={styles.header}>{header}</div>}

      {rows && rows.length > 0 && (
        <div className={styles.body}>
          {rows.map((row, i) => (
            <div key={i} className={styles.row}>
              <span className={styles.swatch} style={{ background: row.color }} />
              <span className={styles.label}>{row.label}</span>
              <span className={styles.value}>{row.value}</span>
              {row.delta && (
                <span className={classNames(styles.delta, styles[row.deltaDir || 'neutral'])}>
                  {row.delta}
                </span>
              )}
            </div>
          ))}
        </div>
      )}

      {children && <div className={styles.body}>{children}</div>}
    </div>
  );

  return createPortal(node, document.body);
};

ChartTooltip.displayName = 'ChartTooltip';
export default ChartTooltip;
